import { type ElementType, type ReactNode, useMemo } from 'react';
import { clsx } from 'clsx';
import { Skeleton } from '../loading/skeleton';
import type { Polymorphic } from '../polymorphism/polymorphic';
import { BaseTypography, type TypographyProps } from '../typography';
import { TypographyProvider } from '../typography/context';
import styles from './pill.module.scss';

export type PillVariant = 'primary' | 'secondary' | 'tertiary' | 'quaternary' | 'danger' | 'neutral' | 'neutral-lt' | 'dark' | 'green';

export type PillAffixIconPosition = 'left' | 'right';

export type PillSize = 'small' | 'medium' | 'large';

export const DEFAULT_PILL_ELEMENT = 'span';

export type DefaultPillElement = typeof DEFAULT_PILL_ELEMENT;

export const defaultPillTypographyBySize: Record<PillSize, TypographyProps> = {
  small: {
    lineHeight: '1rem',
    size: '0.625rem',
    weight: 'medium',
  },
  medium: {
    lineHeight: '1.25rem',
    size: '0.75rem',
    weight: 'medium',
  },
  large: {
    lineHeight: '1.25rem',
    size: '0.75rem',
    weight: 'medium',
  },
};

interface BasePillProps {
  affixIcon?: ReactNode;
  affixIconPosition?: PillAffixIconPosition;
  isPending?: boolean;
  fillParent?: boolean;
  loadingCharacters?: number;
  size?: PillSize;
  typographyProps?: TypographyProps;
  variant?: PillVariant;
}

export type PillProps<AsComponent extends ElementType = DefaultPillElement> = Polymorphic.Props<AsComponent, BasePillProps>;

export function BasePill<AsComponent extends ElementType = DefaultPillElement>({
  affixIcon,
  affixIconPosition = 'right',
  as,
  children,
  className,
  isPending,
  fillParent,
  loadingCharacters = 6,
  onClick,
  ref,
  size = 'medium',
  typographyProps = defaultPillTypographyBySize[size],
  variant = 'neutral',
  ...rest
}: PillProps<AsComponent>) {
  const Component = (as ?? (onClick ? 'button' : 'span')) satisfies ElementType;
  const sharedClassName = clsx(styles[`size-${size}`], fillParent && styles.fillParent, className);

  if (isPending) {
    return <Skeleton charCount={loadingCharacters} parentClassName={clsx(styles.pillSkeleton, sharedClassName)} type="line" />;
  }

  return (
    <Component
      {...rest}
      className={clsx(
        styles.pill,
        sharedClassName,
        styles[`variant-${variant}`],
        affixIcon && styles.hasAffixIcon,
        affixIcon && styles[`affix-${affixIconPosition}`],
        onClick && styles.isInteractive,
      )}
      onClick={onClick}
      ref={ref}
      type={onClick ? 'button' : undefined}
    >
      {affixIcon && affixIconPosition === 'left' ? <span className={styles.affixIcon}>{affixIcon}</span> : null}
      {['number', 'string'].includes(typeof children) ? (
        <BaseTypography {...defaultPillTypographyBySize[size]} {...(typographyProps as typeof defaultPillTypographyBySize)}>
          {children}
        </BaseTypography>
      ) : (
        children
      )}
      {affixIcon && affixIconPosition === 'right' ? <span className={styles.affixIcon}>{affixIcon}</span> : null}
    </Component>
  );
}

export function Pill<AsComponent extends ElementType = DefaultPillElement>({
  as,
  children,
  onClick,
  size = 'medium',
  typographyProps = defaultPillTypographyBySize[size],
  ...rest
}: PillProps<AsComponent>) {
  const Component = (as ?? (onClick ? 'button' : 'span')) satisfies ElementType;
  const mergedTypographyProps = useMemo(
    () =>
      ({
        tone: 'inherit',
        ...defaultPillTypographyBySize[size],
        ...typographyProps,
      }) as TypographyProps,
    [typographyProps, size],
  );

  return (
    <TypographyProvider value={mergedTypographyProps}>
      <BasePill as={Component} onClick={onClick} size={size} typographyProps={typographyProps} {...rest}>
        {children}
      </BasePill>
    </TypographyProvider>
  );
}
